import React, { useState, useEffect, useContext } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../services/firebase';
import AppContext from '../../contexts/AppContext';

/**
 * Handles Firebase email action links (password reset).
 * Rendered at /action?mode=resetPassword&oobCode=...
 */
const AuthActionScreen = ({ onBackToLogin }) => {
  const { effectiveTheme } = useContext(AppContext);

  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');

  const [step, setStep] = useState('loading'); // loading | form | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) {
      setErrorMsg('Invalid or missing reset link. Please request a new one.');
      setStep('error');
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setStep('form');
      })
      .catch(() => {
        setErrorMsg('This reset link is invalid or has expired. Please request a new one.');
        setStep('error');
      });
  }, [mode, oobCode]);

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setErrorMsg('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStep('success');
    } catch (err) {
      setErrorMsg('Failed to reset password. The link may have expired — please request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-black rounded-lg shadow-xl border border-gray-600 p-8 my-4">

          {/* Logo */}
          <div className="flex items-center justify-center space-x-3 mb-8">
            <img src="/logos/logo-transparent-smll.png" alt="" className="h-12 w-auto object-contain" />
            <img
              src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
              alt="Loft"
              className="h-12 w-auto object-contain"
            />
          </div>

          {step === 'loading' && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#22c55e] mb-4" />
              <p className="text-white">Verifying reset link...</p>
            </div>
          )}

          {step === 'form' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-white text-center">Set New Password</h2>
              {email && <p className="text-sm text-gray-400 text-center">{email}</p>}

              <div>
                <label className="block text-sm font-medium text-white mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-1">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPw ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                    placeholder="Repeat your password"
                    className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  />
                  <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {errorMsg && <p className="text-red-500 text-sm text-center">{errorMsg}</p>}

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Reset Password'}
              </button>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-[#16a34a]/20 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Password Reset!</h2>
              <p className="text-gray-400 text-sm">Your password has been updated successfully.</p>
              <button
                onClick={onBackToLogin}
                className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg font-medium hover:bg-[#15803d]"
              >
                Back to Login
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Link Expired</h2>
              <p className="text-gray-400 text-sm">{errorMsg}</p>
              <button
                onClick={onBackToLogin}
                className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg font-medium hover:bg-[#15803d]"
              >
                Back to Login
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AuthActionScreen;
